import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth';
import { RealtimeChannel } from '@supabase/supabase-js';
import { TABLE_NAMES } from '@/lib/supabase-table-names';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { retryRPC } from '@/utils/retryRPC';
import { addPendingReadMark, removePendingReadMark, getPendingReadMarks } from '@/utils/pendingReadMarks';
import { shouldLog } from '@/utils/debugConfig';
import { chatToastCoordinator } from '@/services/ChatToastCoordinator';

// ✅ Track pending unread increments (real-time messages received while data is being fetched)
const pendingUnreadIncrementsRef = { current: new Map<string, number>() };

interface OptimizedConversation {
  id: string;
  title: string;
  conversation_type: string;
  conversation_category: string;
  access_level: string;
  avatar_url?: string;
  description?: string;
  company_id?: string;
  job_id?: string;
  job_application_id?: string;
  job_title?: string;
  company_name?: string;
  company_logo_url?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  message_count: number;
  last_message_at: string;
  last_message_preview?: string;
  unread_count: number;
  last_read_at: string;
  // Simplified avatar and participant data
  profile_avatar_url?: string;
  display_name?: string;
  // Role badge for UI display
  role_badge?: {
    user_type: string;
    participant_role: string;
    display_name: string;
  };
  // Role-based display properties
  displaySubtitle?: string;
  isCandidate?: boolean;
  originalJobTitle?: string;
  candidateName?: string;
  candidate_user_id?: string;
  // Application stage information
  current_stage_title?: string;
  current_stage_status?: string;
  current_stage_id?: string;
  // Group chat metadata
  participantCount?: number;
  otherParticipantsPreview?: string[];
}

interface OptimizedMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_avatar_url?: string;
  sender_type: string;
  content: string;
  message_type: string;
  rich_content?: any;
  reply_to_id?: string;
  thread_id?: string;
  attachments: any[];
  reactions: any;
  encrypted: boolean;
  created_at: string;
  updated_at: string;
  edited_at?: string;
  deleted_at?: string;
  // Enhanced fields for role identification
  sender_user_type?: string;
  sender_participant_role?: string;
}

interface TypingIndicator {
  user_id: string;
  user_name: string;
  started_at: string;
}

interface UserPresence {
  user_id: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  last_seen: string;
}

// LRU Cache for messages - PHASE 4: Increased cache size for better performance
class MessageCache {
  private cache = new Map<string, OptimizedMessage[]>();
  private readonly maxSize = 100; // Increased from 20 to 100 conversations
  
  get(conversationId: string): OptimizedMessage[] | undefined {
    const messages = this.cache.get(conversationId);
    if (messages) {
      // Move to end (most recently used)
      this.cache.delete(conversationId);
      this.cache.set(conversationId, messages);
    }
    return messages;
  }
  
  set(conversationId: string, messages: OptimizedMessage[]): void {
    // Remove oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(conversationId, messages);
  }
  
  update(conversationId: string, newMessage: OptimizedMessage): void {
    const messages = this.get(conversationId);
    if (messages) {
      // Add new message and maintain chronological order
      const updatedMessages = [...messages, newMessage].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      this.set(conversationId, updatedMessages);
    }
  }
  
  clear(): void {
    this.cache.clear();
  }

  clearConversation(conversationId: string): void {
    this.cache.delete(conversationId);
  }

  hasValidMessages(conversationId: string): boolean {
    const messages = this.get(conversationId);
    return messages && messages.length > 0;
  }
}

// Application conversation group interface
interface ApplicationGroup {
  id: string;
  job_id: string;
  job_title: string;
  company_name: string;
  company_logo_url?: string;
  conversations: OptimizedConversation[];
  mostRecentMessage: string;
  mostRecentMessageAt: string;
  totalUnreadCount: number;
  applicantCount: number;
}

export const useOptimizedChat = (onNewMessageReceived?: (conversationId: string) => boolean) => {
  const { user } = useUser();
  const toast = useTranslatedToast();
  const [conversations, setConversations] = useState<OptimizedConversation[]>([]);
  const [applicationGroups, setApplicationGroups] = useState<ApplicationGroup[]>([]);
  const [messages, setMessages] = useState<Map<string, OptimizedMessage[]>>(new Map());
  const [loading, setLoading] = useState(false); // PHASE 5: Fixed - Initialize as false
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Map<string, UserPresence>>(new Map());
  const [typingUsers, setTypingUsers] = useState<Map<string, TypingIndicator[]>>(new Map());
  const [error, setError] = useState<string | null>(null); // PHASE 5: Error state

  // Remove console logging during startup to reduce noise
  
  // Refs for real-time subscriptions
  const conversationsChannelRef = useRef<RealtimeChannel | null>(null);
  const messagesChannelsRef = useRef<Map<string, RealtimeChannel>>(new Map());
  const presenceChannelRef = useRef<RealtimeChannel | null>(null);
  const messageCacheRef = useRef(new MessageCache());
  const typingTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const isFetchingRef = useRef(false); // Track fetch state separately
  const fetchConversationsFallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Debounced fallback
  const conversationsRef = useRef<OptimizedConversation[]>([]); // Track conversations for toast coordinator
  const onNewMessageReceivedCallback = useRef(onNewMessageReceived); // Callback to check window visibility

  // Memoized user profile for efficient display
  const userProfile = useMemo(() => ({
    id: user?.id || '',
    name: user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'You',
    avatar_url: user?.user_metadata?.avatar_url || null,
  }), [user]);

  // Helper: Format display name from user profile
  const formatDisplayName = useCallback((profile: any): string => {
    if (!profile) return 'User';
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile.first_name || profile.last_name || profile.email?.split('@')[0] || 'User';
  }, []);

  // PHASE 2: Optimized helper for fetching direct chat participants
  const fetchDirectChatParticipants = useCallback(async (
    directConvIds: string[],
    currentUserId: string
  ): Promise<Map<string, {
    user_id?: string;
    profile_avatar_url?: string;
    display_name?: string;
    user_type?: string;
    participant_role?: string;
    participantCount: number;
    otherParticipantsPreview?: string[];
  }>> => {
    if (directConvIds.length === 0) return new Map();

    if (shouldLog('verbose')) {
      console.log('🔍 Phase 2: Fetching direct chat participants (optimized)', { count: directConvIds.length });
    }

    const { data: directParticipants, error } = await supabase
      .from('conversation_participants')
      .select(`
        conversation_id,
        user_id,
        user_type,
        participant_role,
        user_profiles!inner(user_id, first_name, last_name, email, avatar_url)
      `)
      .in('conversation_id', directConvIds as any)
      .neq('user_id', currentUserId as any); // CRITICAL: Only fetch OTHER participant for efficiency

    if (error) {
      console.error('❌ Error fetching direct chat participants:', error);
      return new Map();
    }

    const participantDataMap = new Map();
    directParticipants?.forEach((p: any) => {
      const profile = p.user_profiles as any;
      participantDataMap.set(p.conversation_id, {
        user_id: p.user_id, // Include user_id for profile viewing
        profile_avatar_url: profile.avatar_url,
        display_name: formatDisplayName(profile),
        user_type: p.user_type,
        participant_role: p.participant_role,
        participantCount: 2 // Direct chats always have exactly 2 participants
      });
    });

    if (shouldLog('verbose')) {
      console.log('✅ Phase 2: Direct chat participants loaded', { conversations: participantDataMap.size });
    }
    return participantDataMap;
  }, [formatDisplayName]);

  // PHASE 2: Optimized helper for fetching group chat participants
  const fetchGroupChatParticipants = useCallback(async (
    groupConvIds: string[],
    currentUserId: string
  ): Promise<Map<string, {
    profile_avatar_url?: string;
    display_name?: string;
    user_type?: string;
    participant_role?: string;
    participantCount: number;
    otherParticipantsPreview?: string[];
  }>> => {
    if (groupConvIds.length === 0) return new Map();

    if (shouldLog('verbose')) {
      console.log('🔍 Phase 2: Fetching group chat participants (optimized)', { count: groupConvIds.length });
    }

    const { data: groupParticipants, error } = await supabase
      .from('conversation_participants')
      .select(`
        conversation_id,
        user_id,
        user_type,
        participant_role,
        user_profiles!inner(user_id, first_name, last_name, email, avatar_url)
      `)
      .in('conversation_id', groupConvIds as any);

    if (error) {
      console.error('❌ Error fetching group chat participants:', error);
      return new Map();
    }

    // Group participants by conversation
    const groupMap = new Map<string, any[]>();
    groupParticipants?.forEach((p: any) => {
      if (!groupMap.has(p.conversation_id)) {
        groupMap.set(p.conversation_id, []);
      }
      groupMap.get(p.conversation_id)!.push(p);
    });

    const participantDataMap = new Map();
    
    // Process each group conversation
    groupMap.forEach((participants, convId) => {
      const otherParticipants = participants.filter(p => p.user_id !== currentUserId);
      const participantCount = participants.length;
      
      // For group chats, create preview of other participants (first 3)
      const otherParticipantsPreview = otherParticipants
        .slice(0, 3)
        .map(p => formatDisplayName(p.user_profiles));

      participantDataMap.set(convId, {
        participantCount,
        otherParticipantsPreview,
        // For avatar, use first other participant or fallback
        profile_avatar_url: otherParticipants[0]?.user_profiles?.avatar_url,
        display_name: otherParticipants[0] ? formatDisplayName(otherParticipants[0].user_profiles) : undefined,
        user_type: otherParticipants[0]?.user_type,
        participant_role: otherParticipants[0]?.participant_role
      });
    });

    if (shouldLog('verbose')) {
      console.log('✅ Phase 2: Group chat participants loaded', { conversations: participantDataMap.size });
    }
    return participantDataMap;
  }, [formatDisplayName]);

// PHASE 2 & 3: Optimized fetch with permission filtering and error handling
  const fetchConversations = useCallback(async (options: { silent?: boolean } = {}) => {
    if (!user?.id) return;
    
    // Prevent concurrent fetches using ref
    if (isFetchingRef.current) {
      if (shouldLog('verbose')) {
        console.log('🔄 Fetch already in progress, skipping');
      }
      return;
    }
    
    if (shouldLog('verbose')) {
      console.log('🔄 Fetching conversations for user:', user.id, { silent: options.silent });
    }
    
    try {
      isFetchingRef.current = true;
      // Only show loading state for user-initiated fetches
      if (!options.silent) {
        setLoading(true);
      }
      setError(null); // PHASE 5: Clear errors on new fetch
      
      // PHASE 3: Enhanced permission filtering with is_active check
      const { data, error: fetchError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          unread_count,
          last_read_at,
          conversations!inner(
            id, title, conversation_type, conversation_category, access_level,
            avatar_url, description, company_id, job_id, job_application_id,
            primary_role_context,
            created_by, created_at, updated_at, is_archived, message_count,
            last_message_at,
            company_profiles(name, logo_url)
          )
        `)
        .eq('user_id', user.id as any)
        .eq('is_active', true as any) // PHASE 3: Only fetch active participants
        .order('last_message_at', { foreignTable: 'conversations', ascending: false });

      if (error) throw error;

      // Get last messages for previews
      const conversationIds = data?.map((item: any) => item.conversation_id) || [];
      
      const { data: lastMessages } = await supabase
        .from('conversation_messages')
        .select('conversation_id, content, message_type, created_at')
        .in('conversation_id', conversationIds as any)
        .order('created_at', { ascending: false });

      // Create preview map with timestamp
      const previewMap = new Map<string, { preview: string; created_at: string }>();
      lastMessages?.forEach((msg: any) => {
        if (!previewMap.has(msg.conversation_id)) {
          let preview = msg.content || '';
          if (msg.message_type === 'file') preview = '📎 File shared';
          else if (msg.message_type === 'image') preview = '🖼️ Image shared';
          else if (preview.length > 50) preview = preview.substring(0, 50) + '...';
          previewMap.set(msg.conversation_id, { preview, created_at: msg.created_at });
        }
      });

      // Split conversations by type for optimized queries (PHASE 2)
      const directConvIds: string[] = [];
      const groupConvIds: string[] = [];
      
      data?.forEach((item: any) => {
        if (item.conversations.conversation_type === 'direct') {
          directConvIds.push(item.conversation_id);
        } else {
          groupConvIds.push(item.conversation_id);
        }
      });

      if (shouldLog('verbose')) {
        console.log('📊 Phase 2: Conversation split', { 
          direct: directConvIds.length, 
          group: groupConvIds.length 
        });
      }

      // PHASE 2: Fetch participants in parallel for better performance
      const [directParticipantsMap, groupParticipantsMap] = await Promise.all([
        fetchDirectChatParticipants(directConvIds, user.id),
        fetchGroupChatParticipants(groupConvIds, user.id)
      ]);

      // Merge the two maps
      const participantDataMap = new Map([
        ...directParticipantsMap,
        ...groupParticipantsMap
      ]);

      if (shouldLog('verbose')) {
        console.log('✅ Phase 2: All participants loaded', { total: participantDataMap.size });
      }

      // Read pending read marks from localStorage
      const pending = getPendingReadMarks();

      const formattedConversations: OptimizedConversation[] = data?.map((item: any) => {
        const participantData = participantDataMap.get(item.conversation_id);
        const isDirect = item.conversations.conversation_type === 'direct';
        
        const previewEntry = previewMap.get(item.conversation_id) as { preview: string; created_at: string } | undefined;
        // FIXED: Robust message detection - has messages if count > 0 OR preview exists
        const hasMessages = item.conversations.message_count > 0 || previewEntry?.preview;
        
        // Check for recent pending read mark (within 60 seconds)
        const pendingRead = pending[item.conversation_id];
        const hasRecentPendingRead = pendingRead && 
          pendingRead.userId === user.id && 
          Date.now() - pendingRead.timestamp < 60000;
        
        // Use pending read timestamp if available, otherwise server last_read_at
        const effectiveLastReadAt = hasRecentPendingRead 
          ? new Date(pendingRead.timestamp).toISOString()
          : item.last_read_at;
        
        // ✅ Apply pending unread increments (from real-time updates received during fetch)
        const pendingIncrement = pendingUnreadIncrementsRef.current.get(item.conversation_id) || 0;
        
        if (shouldLog('verbose')) {
          console.log('💬 Conv message check:', {
            id: item.conversation_id.substring(0, 8),
            title: item.conversations.title,
            message_count: item.conversations.message_count,
            hasPreview: !!previewEntry?.preview,
            hasMessages
          });
        }
        
        // Clear cache for conversations without messages
        if (!hasMessages) {
          messageCacheRef.current.clearConversation(item.conversation_id);
        }
        
        // PHASE 5: Client-side unread count calculation as fallback
        // If last_message_at is newer than last_read_at, there should be at least 1 unread
        const lastMessageTime = previewEntry?.created_at || item.conversations.last_message_at;
        const hasUnreadByTimestamp = lastMessageTime && effectiveLastReadAt && 
          new Date(lastMessageTime) > new Date(effectiveLastReadAt);
        
        let calculatedUnreadCount: number;
        
        // Force unread=0 if we have a recent pending read mark
        if (hasRecentPendingRead) {
          calculatedUnreadCount = 0;
          if (shouldLog('verbose')) {
            console.log('🟢 Pending read applied - forcing unread=0:', {
              conversationId: item.conversation_id.substring(0, 8),
              title: item.conversations.title,
              pendingReadTimestamp: new Date(pendingRead.timestamp).toISOString(),
              effectiveLastReadAt,
              lastMessageTime
            });
          }
        } else {
          // Use the maximum of database unread_count and client-calculated indicator
          // This ensures we never show 0 when there are actually unread messages
          calculatedUnreadCount = hasUnreadByTimestamp && item.unread_count === 0 ? 1 : item.unread_count;
          
          // Debug logging when fallback is used
          if (shouldLog('critical') && hasUnreadByTimestamp && item.unread_count === 0) {
            console.warn('🔧 Unread count fallback triggered:', {
              conversationId: item.conversation_id.substring(0, 8),
              title: item.conversations.title,
              dbUnreadCount: item.unread_count,
              calculatedCount: calculatedUnreadCount,
              lastMessageTime,
              lastReadTime: effectiveLastReadAt
            });
          }
        }
        
        const conversation = {
          ...item.conversations,
          // FIXED: Derive last_message_at from preview or fallback to existing timestamps
          last_message_at: previewEntry?.created_at || item.conversations.last_message_at || item.conversations.updated_at,
          // Use profile_avatar_url as primary avatar source
          profile_avatar_url: participantData?.profile_avatar_url,
          display_name: participantData?.display_name,
          // For direct chats, ALWAYS use other participant's name
          // For group chats, use conversation title or participant preview
          title: isDirect && participantData?.display_name 
            ? participantData.display_name 
            : item.conversations.title,
          // For direct chats, include other participant's user_id and user_type for profile viewing
          other_participant_user_id: isDirect && participantData ? (participantData as any).user_id : undefined,
          other_participant_user_type: isDirect ? participantData?.user_type : undefined,
          // Fetch company data from join
          company_logo_url: item.conversations.company_profiles?.logo_url,
          company_name: item.conversations.company_profiles?.name,
          // ✅ Apply pending unread increments only if no recent pending read
          unread_count: calculatedUnreadCount + (hasRecentPendingRead ? 0 : pendingIncrement),
          last_read_at: effectiveLastReadAt,
          // FIXED: Show preview if available, otherwise empty (don't zero out if messages exist)
          last_message_preview: previewEntry?.preview || '',
          // FIXED: Keep original message_count - don't zero it out
          message_count: item.conversations.message_count,
          // Create simple role badge if needed
          role_badge: participantData?.user_type ? {
            user_type: participantData.user_type,
            participant_role: participantData.participant_role || 'participant',
            display_name: participantData.display_name || 'User'
          } : undefined,
          // Group chat metadata
          participantCount: participantData?.participantCount,
          otherParticipantsPreview: participantData?.otherParticipantsPreview,
        };

        return conversation;
      }) || [];

      // Fetch job titles directly from job_postings for conversations with job_id
      let enhancedConversations = formattedConversations;
      
      const jobIds = Array.from(new Set(
        enhancedConversations
          .filter(c => !!c.job_id)
          .map(c => c.job_id as string)
      ));

      if (jobIds.length > 0) {
        const { data: jobs } = await supabase
          .from(TABLE_NAMES.JOB_POSTINGS)
          .select('id, title, company_id, company_profiles(name, logo_url)')
          .in('id', jobIds as any);

        if (jobs) {
          const jobMap = new Map<string, { title: string; company_name?: string; company_logo_url?: string }>(
            jobs.map((j: any) => [j.id, { 
              title: j.title, 
              company_name: j.company_profiles?.name,
              company_logo_url: j.company_profiles?.logo_url
            }])
          );
          
          enhancedConversations = enhancedConversations.map(c => {
            if (c.job_id) {
              const jobData = jobMap.get(c.job_id);
              return {
                ...c,
                job_title: jobData?.title,
                // Override company data from job if not already set
                company_name: c.company_name || jobData?.company_name,
                company_logo_url: c.company_logo_url || jobData?.company_logo_url
              };
            }
            return c;
          });
        }
      }

      // Apply role-based data using batched lookups (simplified and robust)
      const applicationIds = Array.from(new Set(
        enhancedConversations
          .filter(c => (c.conversation_type === 'application' || c.conversation_category === 'application' || c.conversation_category === 'hiring') && !!c.job_application_id)
          .map(c => c.job_application_id as string)
      ));
      
      let appMap = new Map<string, any>();
      let profileMap = new Map<string, any>();
      let teamCompanyIds = new Set<string>();
      
      if (applicationIds.length > 0) {
        const { data: appRows } = await supabase
          .from('company_job_applications')
          .select(`
            id, candidate_id, job_id, current_stage_id, current_stage_status,
            company_job_postings!inner(
              id, user_id, title, company_id, job_pipeline_config,
              company_profiles!inner(name, logo_url)
            )
          `)
          .in('id', applicationIds as any);
        
        if (appRows && appRows.length > 0) {
          appMap = new Map(appRows.map((r: any) => [r.id, r]));
          
          const candidateIds = Array.from(new Set(appRows.map((r: any) => r.candidate_id).filter(Boolean)));
          if (candidateIds.length > 0) {
            const { data: candidateProfiles } = await supabase
              .from('user_profiles')
              .select('user_id, first_name, last_name, avatar_url')
              .in('user_id', candidateIds);
            if (candidateProfiles) {
              profileMap = new Map(candidateProfiles.map((p: any) => [p.user_id, p]));
            }
          }
          
          const companyIds = Array.from(new Set(appRows.map((r: any) => r.company_job_postings.company_id).filter(Boolean)));
          if (companyIds.length > 0) {
            const { data: memberships } = await supabase
              .from('company_team_members')
              .select('company_id')
              .eq('user_id', user.id as any)
              .in('company_id', companyIds as any);
            if (memberships) {
              teamCompanyIds = new Set(memberships.map((m: any) => m.company_id));
            }
          }
        }
      }
      
      const processedConversations: OptimizedConversation[] = enhancedConversations.map((conv) => {
        const isAppConv = (conv.conversation_type === 'application' || conv.conversation_category === 'application' || conv.conversation_category === 'hiring') && !!conv.job_application_id;
        if (!isAppConv) return conv;
        
        const app = appMap.get(conv.job_application_id);
        if (!app) return conv;
        
        const candidateId = app.candidate_id as string;
        const jobTitle = app.company_job_postings.title as string;
        const companyName = app.company_job_postings.company_profiles.name as string;
        const companyLogo = app.company_job_postings.company_profiles.logo_url as string | undefined;
        
        const prof = profileMap.get(candidateId);
        const candidateName = `${prof?.first_name || ''} ${prof?.last_name || ''}`.trim() || 'Candidate';
        const candidateAvatar = prof?.avatar_url;
        
        const isCandidate = candidateId === user.id;
        const isJobOwner = app.company_job_postings.user_id === user.id;
        const isHirer = isJobOwner || teamCompanyIds.has(app.company_job_postings.company_id);
        
        // Derive stage title from pipeline config
        let stageTitle = app.current_stage_id;
        if (app.company_job_postings.job_pipeline_config && app.current_stage_id) {
          const pipelineConfig = app.company_job_postings.job_pipeline_config;
          if (Array.isArray(pipelineConfig)) {
            const stage = pipelineConfig.find((s: any) => s.id === app.current_stage_id);
            if (stage) {
              stageTitle = stage.stage_config?.title || stage.title || stage.id;
            }
          }
        }
        
        return {
          ...conv,
          isCandidate,
          candidate_user_id: candidateId,
          candidateName,
          job_title: jobTitle,
          originalJobTitle: jobTitle,
          company_name: companyName,
          company_logo_url: companyLogo || conv.company_logo_url,
          current_stage_id: app.current_stage_id,
          current_stage_status: app.current_stage_status || 'pending',
          current_stage_title: stageTitle,
          // Presentation
          title: isCandidate ? jobTitle : (isHirer ? candidateName : (conv.title || candidateName)),
          displaySubtitle: isCandidate ? companyName : (isHirer ? jobTitle : (conv.displaySubtitle || companyName)),
          profile_avatar_url: isCandidate ? (companyLogo || conv.profile_avatar_url) : (isHirer ? (candidateAvatar || conv.profile_avatar_url) : conv.profile_avatar_url),
        };
      });
      
      // Use processedConversations directly for now - stage info will be added via props when opening chat
      const flattenedConversations = processedConversations;

      // Remove duplicates by conversation ID and sort by last_message_at
      const uniqueConversations = flattenedConversations.reduce((acc, conv) => {
        const existing = acc.find(c => c.id === conv.id);
        if (!existing) {
          acc.push(conv);
        }
        return acc;
      }, [] as OptimizedConversation[]);

      const sortedConversations = uniqueConversations.sort((a, b) => 
        new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );

      console.log('📋 Total conversations fetched:', sortedConversations.length);

      setConversations(sortedConversations);
      conversationsRef.current = sortedConversations; // Update ref for toast coordinator
      
      // ✅ Clear pending unread increments after applying them
      pendingUnreadIncrementsRef.current.clear();
      
      // Process application conversations into groups
      processApplicationGroups(flattenedConversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [user?.id]); // Remove loading from dependencies to prevent cycle

  // Process application conversations into job-based groups - for ALL applications, not filtered by role
  const processApplicationGroups = useCallback((conversations: OptimizedConversation[]) => {
    if (!user?.id) return;
    
    const applicationConversations = conversations.filter(conv => 
      conv.conversation_type === 'application' || 
      conv.conversation_category === 'application' ||
      conv.conversation_category === 'hiring'
    );

    // Group ALL application conversations by job_id
    // The filtering of what to show (individual vs grouped) happens in useFloatingChatLogic
    // Here we just need to create comprehensive groups that include all candidates

    // Group by job_id
    const groupsMap = new Map<string, ApplicationGroup>();
    
    applicationConversations.forEach(conv => {
      if (!conv.job_id) return; // Skip conversations without job_id
      
      const groupId = conv.job_id;
      
      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          id: groupId,
          job_id: conv.job_id,
          job_title: conv.job_title || 'Unknown Position',
          company_name: conv.company_name || 'Unknown Company',
          company_logo_url: conv.company_logo_url,
          conversations: [],
          mostRecentMessage: '',
          mostRecentMessageAt: '',
          totalUnreadCount: 0,
          applicantCount: 0
        });
      }
      
      const group = groupsMap.get(groupId)!;
      group.conversations.push(conv);
      if (!group.company_logo_url && conv.company_logo_url) {
        group.company_logo_url = conv.company_logo_url;
      }
      group.totalUnreadCount += conv.unread_count;
      
      // Update most recent message
      if (!group.mostRecentMessageAt || 
          new Date(conv.last_message_at) > new Date(group.mostRecentMessageAt)) {
        // FIXED: Show 'New message' if message_count > 0 but no preview
        group.mostRecentMessage = conv.last_message_preview || (conv.message_count > 0 ? 'New message' : 'No messages yet');
        group.mostRecentMessageAt = conv.last_message_at;
      }
    });

    // Deduplicate conversations by candidate_user_id within each group (robust)
    groupsMap.forEach(group => {
      const candidateMap = new Map<string, OptimizedConversation>();
      
      group.conversations.forEach(conv => {
        const key = conv.candidate_user_id || `conv:${conv.id}`;
        const existing = candidateMap.get(key);
        if (!existing) {
          candidateMap.set(key, conv);
          return;
        }
        // If both exist for same candidate, prefer hirer view over candidate view
        if (!conv.isCandidate && existing.isCandidate) {
          candidateMap.set(key, conv);
        }
      });
      
      group.conversations = Array.from(candidateMap.values());
      group.applicantCount = group.conversations.length;
    });

    // Convert to array and sort by most recent activity
    const groups = Array.from(groupsMap.values()).sort((a, b) => 
      new Date(b.mostRecentMessageAt).getTime() - new Date(a.mostRecentMessageAt).getTime()
    );

    setApplicationGroups(groups);
  }, [user?.id]);

  // Fetch messages with cursor-based pagination
  const fetchMessages = useCallback(async (
    conversationId: string, 
    cursor?: string, 
    direction: 'before' | 'after' = 'before'
  ) => {
    if (!conversationId) return;

    // Check cache first
    const cachedMessages = messageCacheRef.current.get(conversationId);
    if (cachedMessages && !cursor) {
      setMessages(prev => new Map(prev.set(conversationId, cachedMessages)));
      return;
    }

    setLoadingMessages(true);
    try {
      // First get the basic messages
      const { data, error } = await supabase.rpc('get_conversation_messages_paginated', {
        conversation_id_param: conversationId,
        cursor_id: cursor || null,
        page_size: 50,
        direction
      });

      if (error) throw error;

      let newMessages = data as OptimizedMessage[];

      // Enhance messages with participant role information for hiring conversations
      if (newMessages.length > 0) {
        const senderIds = [...new Set(newMessages.map(msg => msg.sender_id))];
        
        const { data: participantData } = await supabase
          .from('conversation_participants')
          .select('user_id, user_type, participant_role')
          .eq('conversation_id', conversationId as any)
          .in('user_id', senderIds as any);

        if (participantData) {
          const participantMap = new Map(
            participantData.map((p: any) => [p.user_id, { user_type: p.user_type, participant_role: p.participant_role }])
          );

          newMessages = newMessages.map(msg => ({
            ...msg,
            sender_user_type: participantMap.get(msg.sender_id)?.user_type,
            sender_participant_role: participantMap.get(msg.sender_id)?.participant_role
          }));
        }
      }
      
      if (cursor) {
        // Append to existing messages
        setMessages(prev => {
          const existing = prev.get(conversationId) || [];
          const combined = direction === 'before' 
            ? [...newMessages.reverse(), ...existing]
            : [...existing, ...newMessages];
          const updated = new Map(prev.set(conversationId, combined));
          messageCacheRef.current.set(conversationId, combined);
          return updated;
        });
      } else {
      // Initial load - validate messages exist
      const sortedMessages = newMessages.reverse();
      if (sortedMessages.length > 0) {
        setMessages(prev => new Map(prev.set(conversationId, sortedMessages)));
        messageCacheRef.current.set(conversationId, sortedMessages);
      } else {
        // Clear any cached data if no messages found
        setMessages(prev => {
          const newMap = new Map(prev);
          newMap.delete(conversationId);
          return newMap;
        });
        messageCacheRef.current.clearConversation(conversationId);
      }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Send message with optimistic update
  const sendMessage = useCallback(async (
    conversationId: string,
    content: string,
    messageType: string = 'text'
  ) => {
    console.log('🔄 [useOptimizedChat] sendMessage called:', {
      conversationId,
      content: content?.substring(0, 50) + (content?.length > 50 ? '...' : ''),
      messageType,
      userId: user?.id,
      userProfile: userProfile?.name
    });

    if (!user?.id || !content.trim()) {
      console.log('🔄 [useOptimizedChat] Message blocked:', {
        hasUser: !!user?.id,
        hasContent: !!content.trim(),
        content: content
      });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: OptimizedMessage = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: userProfile.name,
      sender_avatar_url: userProfile.avatar_url,
      sender_type: 'user',
      content: content.trim(),
      message_type: messageType,
      attachments: [],
      reactions: {},
      encrypted: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    console.log('🔄 [useOptimizedChat] Created optimistic message:', optimisticMessage);

    // Optimistic update
    setMessages(prev => {
      const existing = prev.get(conversationId) || [];
      const updated = [...existing, optimisticMessage];
      const newMap = new Map(prev.set(conversationId, updated));
      console.log('🔄 [useOptimizedChat] Updated messages optimistically, total:', updated.length);
      return newMap;
    });

    try {
      console.log('🔄 [useOptimizedChat] Inserting to database...');
      const { data, error } = await supabase
        .from('conversation_messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          sender_name: userProfile.name,
          sender_avatar_url: userProfile.avatar_url,
          sender_type: 'user',
          content: content.trim(),
          message_type: messageType,
        } as any)
        .select()
        .single();

      if (error) {
        console.error('🔄 [useOptimizedChat] Database insert error:', error);
        throw error;
      }

      console.log('🔄 [useOptimizedChat] Message inserted successfully:', data);

      // Replace optimistic message with real one
      setMessages(prev => {
        const existing = prev.get(conversationId) || [];
        const updated = existing.map(msg => 
          msg.id === tempId ? (data as any) as OptimizedMessage : msg
        );
        const newMap = new Map(prev.set(conversationId, updated));
        messageCacheRef.current.set(conversationId, updated);
        console.log('🔄 [useOptimizedChat] Replaced optimistic message with real one');
        return newMap;
      });

      // Update conversation last message
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, last_message_at: (data as any).created_at, message_count: conv.message_count + 1 }
            : conv
        )
      );

    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message on error
      setMessages(prev => {
        const existing = prev.get(conversationId) || [];
        const updated = existing.filter(msg => msg.id !== tempId);
        return new Map(prev.set(conversationId, updated));
      });
    }
  }, [user?.id, userProfile]);

  // Mark conversation as read (optimistic updates with no real-time trigger)
  const markAsRead = useCallback(async (conversationId: string) => {
    if (!user?.id) return;

    try {
      // ✅ Clear pending unread increments when marking as read
      pendingUnreadIncrementsRef.current.delete(conversationId);
      
      // Store in localStorage before RPC (Phase 4: persistence across refreshes)
      addPendingReadMark(conversationId, user.id);

      // Optimistic update - clear unread count immediately
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === conversationId 
            ? { ...conv, unread_count: 0, last_read_at: new Date().toISOString() }
            : conv
        )
      );

      // Update application groups optimistically too
      setApplicationGroups(prevGroups => 
        prevGroups.map(group => ({
          ...group,
          conversations: group.conversations.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unread_count: 0, last_read_at: new Date().toISOString() }
              : conv
          ),
          totalUnreadCount: group.conversations.reduce((sum, conv) => 
            sum + (conv.id === conversationId ? 0 : conv.unread_count), 0
          )
        }))
      );

      // Phase 3: Retry RPC with exponential backoff
      const { error } = await retryRPC(async () => {
        const { data, error } = await supabase.rpc('mark_conversation_as_read_silent', {
          p_conversation_id: conversationId,
          p_user_id: user.id
        });
        return { data, error };
      });

      if (error) {
        console.error('❌ Failed to mark as read in database:', error);
        // Phase 5: User feedback
        toast.raw.error('Failed to mark as read', {
          description: 'Your changes may not be saved. Please try again.'
        });
        // Revert optimistic update
        fetchConversations({ silent: true }); // Silent revert on error
        throw error;
      }

      // Phase 4: Remove from localStorage on success
      removePendingReadMark(conversationId);
      if (shouldLog('verbose')) {
        console.log('✅ Successfully marked as read and cleared pending mark:', conversationId);
      }

    } catch (error) {
      console.error('Error marking as read:', error);
      // Revert to database state
      fetchConversations({ silent: true }); // Silent revert on error
    }
  }, [user?.id, fetchConversations]);

  // Typing indicator management
  const sendTypingIndicator = useCallback(async (conversationId: string) => {
    if (!user?.id) return;

    try {
      await supabase
        .from('conversation_typing_indicators')
        .upsert({
          conversation_id: conversationId,
          user_id: user.id,
          user_name: userProfile.name,
          expires_at: new Date(Date.now() + 10000).toISOString(), // 10 seconds
        } as any);
    } catch (error) {
      console.error('Error sending typing indicator:', error);
    }
  }, [user?.id, userProfile.name]);

  // Debounced typing indicator
  const handleTyping = useCallback((conversationId: string) => {
    // Clear existing timeout
    const existing = typingTimeoutRef.current.get(conversationId);
    if (existing) {
      clearTimeout(existing);
    }

    // Send typing indicator
    sendTypingIndicator(conversationId);

    // Set new timeout to stop typing
    const timeout = setTimeout(() => {
      typingTimeoutRef.current.delete(conversationId);
    }, 3000);

    typingTimeoutRef.current.set(conversationId, timeout);
  }, [sendTypingIndicator]);

  // Set up real-time subscriptions with optimized update strategy
  useEffect(() => {
    if (!user?.id) return;

    // Conversations subscription - only listen to significant changes
    const conversationsChannel = supabase
      .channel('conversations-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          console.log('🔄 New conversation:', payload);
          fetchConversations(); // Refetch for new conversations
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          console.log('🔄 Conversation updated:', payload);
          // Only refetch if it's a significant update (not just last_message_at)
          const record = payload.new as any;
          if (record && (record.title !== payload.old?.title || record.is_archived !== payload.old?.is_archived)) {
            fetchConversations({ silent: true }); // Silent update for background changes
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_participants'
        },
        (payload) => {
          console.log('🔄 New participant added:', payload);
          fetchConversations({ silent: true }); // Silent update for background changes
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'conversation_participants'
        },
        (payload) => {
          console.log('🔄 Participant removed:', payload);
          fetchConversations({ silent: true }); // Silent update for background changes
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_messages'
        },
        (payload) => {
          const newMessage = payload.new as OptimizedMessage;
          const conversationId = newMessage.conversation_id;
          
          // Only update if message is NOT from current user
          if (newMessage.sender_id !== user?.id) {
            console.log('🔔 Global realtime: New message INSERT from other user', {
              conversationId,
              sender: newMessage.sender_id,
              messageType: newMessage.message_type
            });
            
            // Generate message preview without relying on outer scope
            const messagePreview = newMessage.message_type === 'file' 
              ? '📎 File shared' 
              : newMessage.message_type === 'image' 
              ? '🖼️ Image shared' 
              : newMessage.content.length > 50 
              ? newMessage.content.substring(0, 50) + '...' 
              : newMessage.content;

            // Show toast notification for messages in closed/minimized windows
            // Find conversation details for better toast message
            const conversation = conversationsRef.current.find(c => c.id === conversationId);
            
            // Check if window is visible via callback (if provided) - stable reference
            const windowVisible = onNewMessageReceivedCallback.current 
              ? onNewMessageReceivedCallback.current(conversationId) 
              : false;

            // Only show toast if window is not visible and debounce allows it
            if (!windowVisible && chatToastCoordinator.canShowToast(conversationId)) {
              const senderName = newMessage.sender_name || 'Someone';
              const conversationTitle = conversation?.title || 'Conversation';
              
              // Determine toast key based on message type
              let toastKey = 'chat.new_message';
              if (newMessage.message_type === 'file') {
                toastKey = 'chat.new_file';
              } else if (newMessage.message_type === 'image') {
                toastKey = 'chat.new_image';
              }
              
              // Show translated toast
              toast.raw.info(`${senderName}: ${messagePreview}`, {
                description: conversationTitle,
                duration: 5000,
              });
              
              // Mark toast as shown to start debounce timer
              chatToastCoordinator.markToastShown(conversationId);
              
              console.log('🔔 Toast shown for new message:', {
                conversationId,
                sender: senderName,
                preview: messagePreview
              });
            }

            let conversationFoundInState = false;
            let applicationGroupFoundInState = false;

            // ✅ Check if window is visible (open AND not minimized) - ONCE for both updates
            // Use stable callback reference to prevent stale closures
            const isWindowVisible = onNewMessageReceivedCallback.current 
              ? onNewMessageReceivedCallback.current(conversationId) 
              : false;
            console.log('📊 Window visibility check:', { conversationId, isWindowVisible });
            
            // Update conversations using functional update (no stale closure)
            setConversations(prev => {
              const conversationExists = prev.some(c => c.id === conversationId);
              if (!conversationExists) {
                return prev; // No change, will trigger fallback
              }
              
              conversationFoundInState = true;
              console.log('✅ Updated in conversations state:', conversationId);
              
              // ✅ Only track pending unread increment if window is NOT visible
              if (!isWindowVisible) {
                pendingUnreadIncrementsRef.current.set(
                  conversationId, 
                  (pendingUnreadIncrementsRef.current.get(conversationId) || 0) + 1
                );
              }
              
              const updatedConversations = prev.map(conv => {
                if (conv.id !== conversationId) return conv;
                
                const newUnreadCount = isWindowVisible ? 0 : conv.unread_count + 1;
                console.log('📊 Conversations unread update:', {
                  conversationId,
                  isWindowVisible,
                  prevUnread: conv.unread_count,
                  newUnread: newUnreadCount
                });
                
                return { 
                  ...conv, 
                  last_message_at: newMessage.created_at,
                  last_message_preview: messagePreview,
                  message_count: conv.message_count + 1,
                  unread_count: newUnreadCount
                };
              });
              
              return updatedConversations;
            });
            
            // Update application groups using functional update (no stale closure)
            setApplicationGroups(prev => {
              const groupWithConv = prev.find(g => 
                g.conversations.some(c => c.id === conversationId)
              );
              
              if (!groupWithConv) {
                return prev; // No change, will trigger fallback
              }
              
              applicationGroupFoundInState = true;
              console.log('✅ Updated in applicationGroups state:', conversationId);
              
              return prev.map(group => {
                const hasConv = group.conversations.some(c => c.id === conversationId);
                if (!hasConv) return group;
                
                // Update conversation within group
                const updatedConversations = group.conversations.map(conv =>
                  conv.id === conversationId
                    ? {
                        ...conv,
                        last_message_at: newMessage.created_at,
                        last_message_preview: messagePreview,
                        // ✅ FIX: Use the same visibility check as conversations state
                        unread_count: isWindowVisible ? 0 : conv.unread_count + 1
                      }
                    : conv
                );
                
                // Recalculate totals from updated conversations
                const totalUnreadCount = updatedConversations.reduce(
                  (sum, c) => sum + c.unread_count, 
                  0
                );
                
                console.log('📊 ApplicationGroups unread update:', {
                  conversationId,
                  isWindowVisible,
                  groupTotalUnread: totalUnreadCount
                });
                
                // FIXED: Resilient comparison - treat empty/invalid mostRecentMessageAt as "older"
                const isNewer = !group.mostRecentMessageAt || 
                                group.mostRecentMessageAt === '' ||
                                new Date(newMessage.created_at) > new Date(group.mostRecentMessageAt);
                
                console.log('🔄 Group message update check:', {
                  groupId: group.id.substring(0, 8),
                  currentMostRecent: group.mostRecentMessageAt,
                  newMessageTime: newMessage.created_at,
                  isNewer
                });
                
                return {
                  ...group,
                  conversations: updatedConversations,
                  totalUnreadCount,
                  // FIXED: Update mostRecentMessage even if previously empty
                  mostRecentMessage: isNewer ? messagePreview : group.mostRecentMessage,
                  mostRecentMessageAt: isNewer ? newMessage.created_at : group.mostRecentMessageAt
                };
              });
            });

            // Fallback: If conversation not found in local state, schedule a refetch
            if (!conversationFoundInState && !applicationGroupFoundInState) {
              console.warn('⚠️ Conversation not found in local state, scheduling fetchConversations fallback:', conversationId);
              
              // Clear existing timeout
              if (fetchConversationsFallbackTimeoutRef.current) {
                clearTimeout(fetchConversationsFallbackTimeoutRef.current);
              }
              
              // Debounce: wait 200ms before refetching (in case multiple messages arrive)
              fetchConversationsFallbackTimeoutRef.current = setTimeout(() => {
                console.log('🔄 Executing fallback fetchConversations due to unknown conversation');
                fetchConversations({ silent: true }); // Silent fallback for unknown conversation
                fetchConversationsFallbackTimeoutRef.current = null;
              }, 200);
            }
          }
        }
      )
      .subscribe();

    conversationsChannelRef.current = conversationsChannel;

    // Presence subscription
    const presenceChannel = supabase
      .channel('online-users')
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        const users = new Map<string, UserPresence>();
        
        Object.entries(state).forEach(([userId, presences]) => {
          const presence = (presences as any[])[0];
          if (presence) {
            users.set(userId, {
              user_id: userId,
              status: presence.status || 'online',
              last_seen: presence.last_seen || new Date().toISOString(),
            });
          }
        });
        
        setOnlineUsers(users);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({
            user_id: user.id,
            status: 'online',
            last_seen: new Date().toISOString(),
          });
        }
      });

    presenceChannelRef.current = presenceChannel;

    return () => {
      conversationsChannel.unsubscribe();
      presenceChannel.unsubscribe();
      // Clean up message channels
      messagesChannelsRef.current.forEach(channel => channel.unsubscribe());
      messagesChannelsRef.current.clear();
      // Clear timeouts
      typingTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
      typingTimeoutRef.current.clear();
      // Clear debounced fallback timeout
      if (fetchConversationsFallbackTimeoutRef.current) {
        clearTimeout(fetchConversationsFallbackTimeoutRef.current);
        fetchConversationsFallbackTimeoutRef.current = null;
      }
    };
  }, [user?.id, fetchConversations]);

  // Phase 4: Process pending read marks on mount (localStorage recovery)
  useEffect(() => {
    if (!user?.id) return;
    
    const pending = getPendingReadMarks();
    Object.entries(pending).forEach(([conversationId, { userId, timestamp }]) => {
      // Only process marks for current user within 1 minute
      if (userId === user.id && Date.now() - timestamp < 60000) {
        markAsRead(conversationId);
      } else {
        // Clean up old/irrelevant marks
        removePendingReadMark(conversationId);
      }
    });
  }, [user?.id, markAsRead]);

  // Subscribe to specific conversation messages
  const subscribeToConversation = useCallback((conversationId: string) => {
    if (messagesChannelsRef.current.has(conversationId)) return;

    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          const newMessage = payload.new as OptimizedMessage;
          
          // Only add if not from current user (avoid duplicate from optimistic update)
          if (newMessage.sender_id !== user?.id) {
            console.log('📨 Per-conversation: Message added to list (unread count managed by global handler)', {
              conversationId,
              messageId: newMessage.id
            });
            
            setMessages(prev => {
              const existing = prev.get(conversationId) || [];
              const updated = [...existing, newMessage];
              const newMap = new Map(prev.set(conversationId, updated));
              messageCacheRef.current.set(conversationId, updated);
              return newMap;
            });
          }

          // Update conversation metadata and preview (NO unread_count update - handled by global subscription)
          const messagePreview = newMessage.message_type === 'file' 
            ? '📎 File shared' 
            : newMessage.message_type === 'image' 
            ? '🖼️ Image shared' 
            : newMessage.content.length > 50 
            ? newMessage.content.substring(0, 50) + '...' 
            : newMessage.content;

          setConversations(prev => 
            prev.map(conv => 
              conv.id === conversationId 
                ? { 
                    ...conv, 
                    last_message_at: newMessage.created_at,
                    last_message_preview: messagePreview,
                    message_count: conv.message_count + 1
                    // ✅ REMOVED: unread_count update - single source of truth is global subscription handler
                  }
                : conv
            )
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_typing_indicators',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const indicator = payload.new as TypingIndicator;
            if (indicator.user_id !== user?.id) {
              setTypingUsers(prev => {
                const existing = prev.get(conversationId) || [];
                const updated = [...existing.filter(t => t.user_id !== indicator.user_id), indicator];
                return new Map(prev.set(conversationId, updated));
              });
            }
          } else if (payload.eventType === 'DELETE') {
            const indicator = payload.old as TypingIndicator;
            setTypingUsers(prev => {
              const existing = prev.get(conversationId) || [];
              const updated = existing.filter(t => t.user_id !== indicator.user_id);
              return new Map(prev.set(conversationId, updated));
            });
          }
        }
      )
      .subscribe();

    messagesChannelsRef.current.set(conversationId, channel);
  }, [user?.id]);

  // Calculate total unread count
  const totalUnreadCount = useMemo(() => 
    conversations.reduce((total, conv) => total + conv.unread_count, 0),
    [conversations]
  );

  // Check if user is online
  const isUserOnline = useCallback((userId: string) => {
    const presence = onlineUsers.get(userId);
    return presence?.status === 'online';
  }, [onlineUsers]);

  // Get typing users for conversation
  const getTypingUsers = useCallback((conversationId: string) => {
    return typingUsers.get(conversationId) || [];
  }, [typingUsers]);

  // Fixed: Initialize with stable reference to prevent dependency loops
  const fetchConversationsRef = useRef(fetchConversations);
  fetchConversationsRef.current = fetchConversations;

  useEffect(() => {
    if (user?.id) {
      fetchConversationsRef.current();
    }
  }, [user?.id]);

  return {
    // Data
    conversations,
    applicationGroups,
    messages,
    onlineUsers,
    userProfile,
    totalUnreadCount,
    
    // Loading states
    loading,
    loadingMessages,
    
    // Actions
    fetchMessages,
    fetchConversations,
    sendMessage,
    markAsRead,
    handleTyping,
    subscribeToConversation,
    isUserOnline,
    getTypingUsers,
  };
};

// Export the ApplicationGroup type for use in components
export type { ApplicationGroup };